import type { TypedDocumentNode } from "@apollo/client";
import type { ListQueryConfig } from "#app/lib/hooks/use-list-query-state";
import type { Omittable } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// The generic entity-list contract
// ---------------------------------------------------------------------------
//
// A RESOURCE is one server-backed noun the app lists: teams, members,
// workflows, issues — and, on the same terms, accounts and contacts when they
// land. It is the ONE place a noun's GraphQL wiring is named. Screens take a
// resource and its columns; they never see a document, a connection path, or a
// codegen type.
//
// Adding a noun is one descriptor in `registry.ts` plus a column set. It is
// never a new hook, a new table, or a new edge mapper.
//
// EVERYTHING here is declared, never derived. The API's list conventions break
// often enough that a layer which guesses the query name, the connection shape
// or the id field from the type name WILL be wrong — read schema.gql:
// six connections carry no `pageInfo`, two name their edge type `*ListEdge`,
// `widgets` and `automationRuns` return a plain array, `trash` returns `Issue`
// nodes, and four lists take neither `where` nor `orderBy`.
//
// This module and its siblings are the boundary: `#app/graphql/generated` is
// imported HERE, and the row types are re-exported as aliases. Nothing above
// `lib/data` imports codegen.

/** Relay connection shape the paginated list queries return. */
export type EntityConnection<TRow> = Omittable<{
  edges: ReadonlyArray<{ node: TRow }>;
  /** Absent on six connections (grep schema.gql to confirm). */
  pageInfo?: { hasNextPage: boolean };
  /** Nullable on seven connections. */
  total?: number | null;
}>;

/** The connection deviations a noun carries, declared so callers can see them. */
export type ConnectionShape = Omittable<{
  /** `IssueSource`, `Reason`, `Automation`, `Role`, `Webhook`, `Workspace`. */
  noPageInfo?: true;
  /** Connection-level fields beyond edges/pageInfo/total, e.g. `unreadCount`. */
  extra?: readonly string[];
  /** The trashed-at stamp on `TrashEdge` — payload that lives on the edge. */
  edgeFields?: readonly string[];
}>;

type ResourceBase = Omittable<{
  /**
   * Stable identity. Namespaces the per-user table-view settings key and the
   * analytics noun, so it is a PERSISTED VALUE — renaming one orphans a user's
   * saved column layout.
   */
  key: string;
  /** Display nouns for empty states, bulk-action copy, and counts. */
  label: { one: string; many: string };
  /** How this list searches and sorts (`lib/filters/list-configs.ts`). */
  query: ListQueryConfig;
  /** The list operation. Explicit — several nouns have no single-item query. */
  document: TypedDocumentNode<any, any>;
  /**
   * Per-group operation for grouped tables — the same selection without the
   * sibling side-queries. Absent when a noun has no grouped view.
   */
  groupDocument?: TypedDocumentNode<any, any>;
}>;

/** A noun whose list query returns a Relay connection. Almost all of them. */
export type ConnectionResource<TRow> = ResourceBase & Omittable<{
  kind: "connection";
  connection: (data: any) => EntityConnection<TRow> | null | undefined;
  shape?: ConnectionShape;
  /**
   * Row identity. Declared per resource, NEVER derived from the type name: the
   * `<noun>Id` twin is a CLOSED legacy set — no new one has landed. Every noun
   * built since the REST swap has `id` only, and the Apollo `typePolicies`
   * key on whichever of the two the noun actually carries.
   */
  rowId: (row: TRow) => string;
}>;

/** A noun whose list query returns a plain array. `widgets`, `automationRuns`. */
export type ArrayResource<TRow> = ResourceBase & {
  kind: "array";
  rows: (data: any) => readonly TRow[];
  rowId: (row: TRow) => string;
};

export type EntityResource<TRow> = ConnectionResource<TRow> | ArrayResource<TRow>;

/** The row type a resource yields — `ResourceRow<typeof TEAMS>`. */
export type ResourceRow<R> = R extends EntityResource<infer TRow> ? TRow : never;

/**
 * Declares a connection-backed resource with its row type inferred from
 * `connection`, so the descriptor never restates the shape the document
 * already carries.
 */
export function defineResource<TRow>(
  resource: Omit<ConnectionResource<TRow>, "kind">,
): ConnectionResource<TRow> {
  return { kind: "connection", ...resource };
}

/** Declares a resource whose list query returns a plain array. */
export function defineArrayResource<TRow>(
  resource: Omit<ArrayResource<TRow>, "kind">,
): ArrayResource<TRow> {
  return { kind: "array", ...resource };
}

/** One page of a list, read uniformly whatever shape the API returned. */
export type EntityPage<TRow> = {
  rows: readonly TRow[];
  /** `null` where the API reports no count. */
  total: number | null;
  /** `false` where the connection carries no `pageInfo` and for plain arrays. */
  hasNextPage: boolean;
};

const EMPTY_ROWS: readonly never[] = [];

/** Reads a page off a query result, whichever list shape the noun uses. */
export function readPage<TRow>(
  resource: EntityResource<TRow>,
  data: unknown,
): EntityPage<TRow> {
  if (resource.kind === "array") {
    return { rows: resource.rows(data) ?? EMPTY_ROWS, total: null, hasNextPage: false };
  }
  const connection = resource.connection(data);
  if (!connection) return { rows: EMPTY_ROWS, total: null, hasNextPage: false };
  return {
    rows: connection.edges.map((edge) => edge.node),
    total: connection.total ?? null,
    hasNextPage: connection.pageInfo?.hasNextPage ?? false,
  };
}

/**
 * Every node of a connection, for the callers that want only the rows.
 *
 * Structural on purpose: `edges` is non-null on most connections and nullable
 * on a few, and this reads both. It is the ONLY sanctioned edges→nodes walk —
 * a second one is how `mapEdges` grew back last time.
 */
export function connectionRows<TRow>(
  connection: { edges?: ReadonlyArray<{ node: TRow }> | null } | null | undefined,
): TRow[] {
  return connection?.edges?.map((edge) => edge.node) ?? [];
}
